import { useCallback } from "react";

import useLocale from "@/hooks/useLocale";

import type { LocalizedField } from "@/utils/types";

export interface Option {
  value: string;
  label: LocalizedField;
}

interface Props {
  onChange: (value: string) => void;
  options: Option[];
  name: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  value?: string;
}

const InputPills = ({
  onChange,
  options,
  name,
  required = false,
  disabled = false,
  value,
}: Props) => {
  const locale = useLocale();

  const handleChange = useCallback(
    (newValue: string) => {
      if (disabled) return;
      onChange(newValue);
    },
    [disabled, onChange],
  );

  const getLabel = (label: LocalizedField) => {
    return label[locale] || label.en || Object.values(label)[0];
  };

  return (
    <div className="space-y-2">
      {options.map((option) => {
        const isSelected = value === option.value;
        const optionLabel = getLabel(option.label);

        return (
          <label
            key={option.value}
            className={`
              flex items-center cursor-pointer transition-all duration-200
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={isSelected}
              onChange={() => handleChange(option.value)}
              disabled={disabled}
              required={required}
              className="sr-only"
            />
            <div
              className={`
                w-5 h-5 rounded-full border-2 mr-3 flex-shrink-0
                ${isSelected
                  ? 'border-blue bg-blue'
                  : 'border-blue bg-white'
                }
              `}
            >
              {isSelected && (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-6 h-4 border-2 border-white bg-blue rounded-full"></div>
                </div>
              )}
            </div>
            <span
              className={`
                text-md font-medium transition-colors duration-200
                ${isSelected ? 'text-blue-600' : 'text-gray-700'}
              `}
            >
              {optionLabel}
            </span>
          </label>
        );
      })}
    </div>
  );
};

export default InputPills;
